import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'

import ConfirmPopup from '../../Popups/ConfirmPopup'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import ToggleField from '../../FormLegacy/FormFieldsV2/ToggleField'
import ImageField from '../../FormLegacy/FormFieldsV2/ImageField'
import TextAreaField from '../../FormLegacy/FormFieldsV2/TextAreaField'
import { getAssetLink } from '../../../helpers/get-asset-link'
import {
  createShopCollectionItem,
  deleteShopCollectionItem,
  selectShopProductById,
  updateShopCollectionItem,
} from '../../../reducers/Organisation/organisationShopReducer'

const OrganisationProductsModal = () => {
  const { t } = useTranslation(['organisation', 'common'])
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { productId } = useParams()

  const product = useSelector((state) => selectShopProductById(state)(productId)) || {}

  const [formPending, setFormPending] = useState(false)
  const [formError, setFormError] = useState('')
  const [modalConfirmOpened, setModalConfirmOpened] = useState(false)

  const isNew = () => productId === 'new'

  const closeModal = () => navigate('/dashboard/organisation/products')

  const openConfirmModal = (e) => {
    e.preventDefault()
    setModalConfirmOpened(true)
  }

  const closeConfirmModal = () => setModalConfirmOpened(false)

  const cancelProductChanges = (e) => {
    e.preventDefault()
    closeModal()
  }

  const saveProduct = ({ id, name, description, active, imageInCategory, image, activeType }) => {
    const productData = {
      name,
      description,
      category: 'Product',
      personal: false,
      active,
      activeType,
    }

    if (imageInCategory.file) {
      productData.imageInCategory = imageInCategory.file
    }
    if (image.file) {
      productData.image = image.file
    }

    setFormPending(true)
    setFormError('')

    const action = isNew()
      ? dispatch(createShopCollectionItem(productData))
      : dispatch(updateShopCollectionItem(productData, id))

    action
      .then(() => {
        closeModal()
        setFormPending(false)
      })
      .catch(() => {
        setFormPending(false)
        setFormError(t('organisation:shared.serverError'))
      })
  }

  const handleDeleteProduct = () => {
    dispatch(deleteShopCollectionItem(product.id))
      .then(() => {
        closeConfirmModal()
        closeModal()
      })
      .catch(() => {
        setFormError(t('organisation:shared.serverError'))
        closeConfirmModal()
      })
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('common:validation.required')),
    description: Yup.string().required(t('common:validation.required')),
  })

  return (
    <div
      className={`modal modal--organisation modal--service modal--product ${
        productId && !isNew() ? 'is-active' : ''
      }`}
    >
      <div className="modal-background" onClick={cancelProductChanges} />
      <div className="modal-content">
        <div className="box">
          <header className="modal__header">
            <span>
              {isNew()
                ? t('organisation:products.modal.titleNew')
                : t('organisation:products.modal.titleEdit')}
            </span>
            <button
              className="modal-close is-large"
              aria-label={t('organisation:shared.closeAriaLabel')}
              onClick={cancelProductChanges}
            />
          </header>
          <Formik
            enableReinitialize
            initialValues={{
              id: product.id || '',
              name: product.name || '',
              active: product.active ?? true,
              activeType: product.activeType ?? true,
              description: product.description || '',
              imageInCategory: {
                file: false,
                image:
                  getAssetLink({
                    path: 'chitown-default',
                    file: product.imageInCategory,
                  }) || '',
              },
              image: {
                file: false,
                image: getAssetLink({ path: 'chitown-default', file: product.image }) || '',
              },
            }}
            onSubmit={saveProduct}
            validationSchema={validationSchema}
          >
            {({ handleSubmit, handleChange, values, errors, touched, setFieldTouched, dirty }) => (
              <form onSubmit={handleSubmit} className="modal__inner form">
                <div className="columns">
                  <div className="column">
                    <div className="modal__section modal__main-info">
                      <div className="modal__name">
                        <TextField
                          name="name"
                          placeholder={t('organisation:products.modal.name.placeholder')}
                          label={t('organisation:products.modal.name.label')}
                          value={values.name}
                          onChange={handleChange}
                          isError={touched.name && !!errors.name}
                          onBlur={() => setFieldTouched('name')}
                        />
                      </div>
                    </div>
                    <div className="modal__section">
                      <ToggleField
                        name="active"
                        label={t('organisation:products.modal.active')}
                        value={values.active}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="modal__section">
                      <ToggleField
                        name="activeType"
                        label={t('organisation:shared.fields.availableOnline')}
                        value={values.activeType}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="modal__section">
                      <TextAreaField
                        name="description"
                        placeholder={t('organisation:products.modal.description.placeholder')}
                        label={t('organisation:products.modal.description.label')}
                        value={values.description}
                        onChange={handleChange}
                        isError={touched.description && !!errors.description}
                        onBlur={() => setFieldTouched('description')}
                      />
                    </div>
                  </div>
                  <div className="column">
                    <div className="modal__section">
                      <ImageField
                        name="imageInCategory"
                        placeholderImage="/images/placeholder.png"
                        imageRatio={3 / 1}
                        onChange={handleChange}
                        value={values.imageInCategory}
                        additionalClass="field-image--big"
                      />
                    </div>
                    <div className="modal__section">
                      <ImageField
                        name="image"
                        placeholderImage="/images/placeholder.png"
                        imageRatio={16 / 9}
                        onChange={handleChange}
                        value={values.image}
                        additionalClass="field-image--big"
                      />
                    </div>
                  </div>
                </div>
                <div className="modal__section modal__actions">
                  {!isNew() && (
                    <span className="link" onClick={openConfirmModal}>
                      {t('organisation:products.modal.delete')}
                    </span>
                  )}
                  <button
                    type="button"
                    className="button button-gray is-block"
                    onClick={cancelProductChanges}
                  >
                    {t('common:actions.cancel')}
                  </button>
                  <button
                    type="submit"
                    className={`button button-primary is-block ${formPending ? 'is-loading' : ''}`}
                    disabled={formPending || !dirty}
                  >
                    {isNew() ? t('organisation:shared.actions.create') : t('common:actions.save')}
                  </button>
                  <p className="help is-danger">{formError}</p>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
      <ConfirmPopup
        title={t('organisation:products.modal.deleteConfirm.title')}
        cancelText={t('common:actions.cancel')}
        confirmText={t('common:actions.confirm')}
        isOpen={modalConfirmOpened}
        cancelCallback={closeConfirmModal}
        confirmCallback={handleDeleteProduct}
      />
    </div>
  )
}

export default OrganisationProductsModal
